// SPEC-467 — cloud-first sync: boot reconciliation + push primitives.
// The server doc (KV doc:<uname>) is the single source of truth; localStorage is an offline
// cache + write buffer. Boot adoption no longer compares savedAt (whole-doc LWW) — a local
// doc with no unsynced edits adopts the cloud unconditionally; unsynced edits go through the
// server-side union merge (SPEC-454 M3, unchanged) and converge on the server result.
// Pure functions + injectable deps (pullDocWithRetry style) so the worker test harness can
// drive the real decision code (precedent: spec454/spec462m2 import web/src modules).

import { retryDelayMs, type FetchDocResult, type PutDocResult } from '../api';
import { buildPersistedDoc, parsePersistedDoc, type AppState } from '../store';

/** The doc-content fields buildPersistedDoc reads — both restore() and parsePersistedDoc
 * outputs satisfy this (AppState does too). */
export type DocParts = Pick<AppState, 'projects' | 'currentId' | 'currentPageId' | 'components' | 'deleted' | 'view'>;

export type ParsedDoc = NonNullable<ReturnType<typeof parsePersistedDoc>>;

/** Same algorithm as the App push fingerprint (SPEC-388): persisted doc with savedAt stripped.
 * Keeping it identical makes "local == last synced" a stable equality check across
 * persist → restore round-trips. */
export function docFingerprint(doc: DocParts): string {
  return JSON.stringify({ ...buildPersistedDoc(doc as AppState), savedAt: 0 });
}

/** SPEC-388 / SPEC-467 G2 — the push-planning decision (pure): null when the persisted
 * fingerprint is unchanged since lastPushedFp (UI-only state churn — selection/busy/jobs/undo
 * stacks — never burns a CF free-tier KV write); otherwise the payload to enqueue. The 10s
 * debounce stays in the caller (unchanged). */
export function planPush(state: AppState, lastPushedFp: string | null): { docJson: string; fingerprint: string } | null {
  const doc = buildPersistedDoc(state);
  const fingerprint = JSON.stringify({ ...doc, savedAt: 0 });
  if (fingerprint === lastPushedFp) return null;
  return { docJson: JSON.stringify(doc), fingerprint };
}

/** lastSyncedFingerprint lives under its OWN localStorage key — never inside buildPersistedDoc
 * output (the doc shape is frozen: SPEC-467 non-goal + G6). Old clients ignore the key =
 * backward compatible (rollback = redeploy previous bundle, zero residue). */
export const LAST_SYNCED_KEY = 'design-cloud-last-synced-fp';

/** Minimal storage surface (window.localStorage satisfies it; tests inject a map). */
export interface FpStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function readLastSyncedFingerprint(storage: FpStorage): string | null {
  try {
    const v = storage.getItem(LAST_SYNCED_KEY);
    return typeof v === 'string' && v ? v : null;
  } catch { return null; }
}

export function writeLastSyncedFingerprint(fp: string, storage: FpStorage): void {
  try { storage.setItem(LAST_SYNCED_KEY, fp); } catch { /* over quota — best effort */ }
}

export type BootReconcileDecision = 'adopt-cloud' | 'keep-local' | 'push-merge';

/** SPEC-467 M1 — boot reconciliation decision (pure). Replaces the old whole-doc savedAt LWW
 * (`!boot.restored || cloud.savedAt > boot.savedAt`), which let a locally-newer device ignore
 * the cloud forever (Dale 2026-10-08: divergent project lists).
 * 'error' pulls never reach this function (G1: caller stays paused, no decision, no push). */
export function decideBootReconcile(input: {
  /** Three-state pull outcome; 'empty' = the server explicitly returned {doc:null}. */
  pull: 'doc' | 'empty';
  /** False = no local archive at all (brand-new device; its seed project must not win — G3). */
  localRestored: boolean;
  /** docFingerprint of the boot-time local doc; null when !localRestored. */
  localFingerprint: string | null;
  /** Fingerprint recorded at the last successful cloud adopt/push; null on pre-467 clients. */
  lastSyncedFingerprint: string | null;
}): BootReconcileDecision {
  // Cloud explicitly empty → keep local; the first push seeds the cloud after the gate opens
  // (current semantics, unchanged).
  if (input.pull === 'empty') return 'keep-local';
  // (a) No local archive → adopt; local synced (fingerprint == lastSynced) → adopt the cloud
  // unconditionally — savedAt plays no role anymore.
  if (!input.localRestored) return 'adopt-cloud';
  // (c) Pre-467 client without lastSyncedFingerprint → treat as unsynced: one push-merge-repull
  // round-trip (server union is lossless; tombstones prevent resurrection).
  if (input.lastSyncedFingerprint === null) return 'push-merge';
  if (input.localFingerprint === input.lastSyncedFingerprint) return 'adopt-cloud';
  // (b) Unsynced local edits → server-side merge, then converge on the server result.
  return 'push-merge';
}

/** SPEC-467 M2 — PUT with exponential-backoff retry (reuses retryDelayMs 5s/15s/45s… cap 120s)
 * until success or session end. Mirrors pullDocWithRetry (SPEC-454 M1). Returns null when
 * alive() flips false (logout/unmount) — the caller must not treat that as a push result.
 * Failures are no longer silent: onRetry fires before every backoff sleep (drives the
 * "offline retrying" indicator). 401 handling is untouched (G7): putDoc resolves ok:false and
 * apiFetch has already fired onUnauthorized. */
export async function putDocWithRetry(deps: {
  putOnce: () => Promise<PutDocResult>;
  sleep: (ms: number) => Promise<void>;
  alive: () => boolean;
  onRetry?: (attempt: number, delayMs: number) => void;
  /** SPEC-469 M2: 服务端终审门确定性 409 拒绝时触发（随后立即返回，零 sleep 零重试）。 */
  onGateRejected?: () => void;
}): Promise<PutDocResult | null> {
  for (let attempt = 0; ; attempt++) {
    if (!deps.alive()) return null;
    const res = await deps.putOnce();
    if (!deps.alive()) return null;
    if (res.ok) return res;
    // SPEC-469 M2（R3，boot reconcile 与 push loop 共用本层）：409 = 确定性拒绝（存量违规已被
    // delta gate 豁免，拦下的只会是新增违规）— 重试永远不会成功：触发 onGateRejected 并立即返回
    // （不 sleep、不重试、不再调度），不再把确定性拒绝谎报成「云端暂时不可达，正在自动重试…」。
    // 401 不入此分支（454 语义逐字不变：apiFetch 已触发 onUnauthorized → 登录面；退避照旧）。
    if (res.status === 409) { deps.onGateRejected?.(); return res; }
    const delay = retryDelayMs(attempt);
    deps.onRetry?.(attempt, delay);
    await deps.sleep(delay);
  }
}

/** Lightweight sync indicator states (SPEC-467 M2). 'synced' renders nothing — success path
 * stays zero-distraction (G5); 'paused' keeps the SPEC-454 M1 cloudPaused toast verbatim.
 * SPEC-469 M2: 'rejected' = 终审门 409 确定性拒绝 — toast-error 同款风格 + cloud.rejected
 * 可操作文案（补审指引）；'rejected' 后下一次本地编辑 enqueuePush 自然重新武装。 */
export type SyncStatus = 'synced' | 'syncing' | 'retrying' | 'paused' | 'rejected';

export interface BootReconcileDeps {
  /** Resolved three-state pull. An 'error' result must never be passed (G1) — defensively
   * it keeps the gate closed. */
  pull: FetchDocResult;
  boot: { restored: boolean; fingerprint: string | null };
  /** Builds the current local doc for the push-merge branch (buildPersistedDoc of live state). */
  localDoc: () => { docJson: string; fingerprint: string };
  /** Rebases the caller's in-memory push fingerprint (App: lastPushedRef) on the doc about to
   * be PUT — same semantics as the debounce push path. Without it the fingerprint-guarded
   * repullAfterMerge can never pass at boot (lastPushedRef starts null and the boot branch
   * never rebased it), so a merged boot push never LOADs the server result: the session UI
   * diverges from the server SoT until the next boot. */
  markPushed: (fp: string) => void;
  lastSynced: { read: () => string | null; write: (fp: string) => void };
  parseCloud: (raw: Record<string, unknown>) => ParsedDoc | null;
  /** LOADs the cloud doc into state, rebases the in-memory push fingerprint and returns it. */
  adopt: (cloud: ParsedDoc) => string;
  putOnce: (docJson: string) => Promise<PutDocResult>;
  /** The existing fingerprint-guarded re-pull (SPEC-454 M3) — adopts the server merge result. */
  repullAfterMerge: () => Promise<void>;
  sleep: (ms: number) => Promise<void>;
  alive: () => boolean;
  onStatus?: (s: SyncStatus) => void;
}

/** SPEC-467 M1 — runs the boot reconciliation to completion. Returns true only when the
 * decision fully completed; the caller opens the cloudReady/push gate on true (nothing is
 * pushed before the decision completes — G3; a push-merge branch that dies mid-retry keeps
 * the gate closed). */
export async function runBootReconcile(deps: BootReconcileDeps): Promise<boolean> {
  if (deps.pull.status === 'error') return false; // unreachable by contract; G1 defense
  if (deps.pull.status === 'empty') return true;  // keep local; first push proceeds as usual
  const cloud = deps.parseCloud(deps.pull.doc);
  // Unparseable cloud doc → current semantics: don't adopt, open the gate (no push-merge
  // against garbage).
  if (!cloud) return true;
  const decision = decideBootReconcile({
    pull: 'doc',
    localRestored: deps.boot.restored,
    localFingerprint: deps.boot.fingerprint,
    lastSyncedFingerprint: deps.lastSynced.read(),
  });
  if (decision === 'adopt-cloud') {
    const fp = deps.adopt(cloud);
    deps.lastSynced.write(fp);
    return true;
  }
  // push-merge (b/c): server-side union merge → converge on the server result.
  const local = deps.localDoc();
  // Rebase the in-memory push fingerprint BEFORE the PUT (App push-path semantics): the
  // guarded repull below only LOADs while the local doc still equals what this push sent.
  deps.markPushed(local.fingerprint);
  deps.onStatus?.('syncing');
  const res = await putDocWithRetry({
    putOnce: () => deps.putOnce(local.docJson),
    sleep: deps.sleep,
    alive: deps.alive,
    onRetry: () => deps.onStatus?.('retrying'),
    onGateRejected: () => deps.onStatus?.('rejected'),
  });
  if (!res) return false; // logged out / unmounted mid-retry → gate stays closed
  // SPEC-469 M2: 409 拒绝 → 不写 lastSynced（未收敛）、不 repull；但返回 true 打开 push 门 —
  // 用户补完审查后下一次本地编辑 enqueuePush 即重新推送（再武装），无需刷新页面。
  if (!res.ok) return true;
  deps.lastSynced.write(local.fingerprint);
  if (res.merged) await deps.repullAfterMerge();
  if (!deps.alive()) return false;
  deps.onStatus?.('synced');
  return true;
}
